// Toast (CONTRACT "Motion table"): one confirmation for copy actions, 120 ms
// opacity and an 8 px rise, bottom centre, raised step. useToast holds a
// single message with a timeout; Toast renders it with role="status".
import { useCallback, useEffect, useRef, useState } from 'react';
import { cx } from './cx';
import './Toast.css';

export const TOAST_DEFAULT_MS = 2400;

export interface ToastState {
  readonly message: string | null;
  readonly show: (message: string, ms?: number) => void;
  readonly dismiss: () => void;
}

export function useToast(): ToastState {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const dismiss = useCallback(() => { clear(); setMessage(null); }, []);
  const show = useCallback((next: string, ms: number = TOAST_DEFAULT_MS) => {
    clear();
    setMessage(next);
    timer.current = setTimeout(() => setMessage(null), ms);
  }, []);
  useEffect(() => clear, []);
  return { message, show, dismiss };
}

export interface ToastProps {
  readonly message: string | null;
  readonly className?: string;
}

export function Toast({ message, className }: ToastProps) {
  return (
    <div role="status" aria-live="polite" className={cx('toast', message === null && 'toast-hidden', className)}>
      {message}
    </div>
  );
}
